import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import {
  AdminTable,
  type AdminTableColumn,
  ConfirmModal,
  FilterToggle,
  Pagination,
  PageHeader,
  SearchBar,
  useDebouncedValue,
} from "../../components/admin/adminPrimitives";
import {
  pgIdList,
  useInternalLicenseIds,
} from "../../components/admin/isAdmin";

interface Activation {
  id: string;
  license_id: string;
  machine_fingerprint: string;
  machine_label: string | null;
  activated_at: string;
  deactivated_at: string | null;
  license_key: string;
  school_name: string;
  account_id: string;
}

const PAGE_SIZE = 25;

export function Activations() {
  const [rows, setRows] = useState<Activation[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingDeactivate, setPendingDeactivate] = useState<Activation | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const [showInternal, setShowInternal] = useState(false);
  const internalLicenseIds = useInternalLicenseIds();

  const fetchRows = useCallback(async () => {
    // Don't fire the main query until we know which licence IDs to
    // exclude, otherwise internal rows flash into view on first paint.
    if (!showInternal && internalLicenseIds === null) return;

    setLoading(true);
    setError(null);
    const from = page * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    let query = supabase
      .from("activations")
      .select(
        `id, license_id, machine_fingerprint, machine_label, activated_at,
         deactivated_at,
         licenses:license_id (
           license_key, account_id,
           accounts:account_id ( school_name )
         )`,
        { count: "exact" },
      )
      .is("deactivated_at", null)
      .order("activated_at", { ascending: false })
      .range(from, to);

    if (!showInternal && internalLicenseIds && internalLicenseIds.length > 0) {
      query = query.not("license_id", "in", pgIdList(internalLicenseIds));
    }

    const q = debouncedSearch.trim();
    if (q) {
      const safe = q.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
      // Supabase doesn't support `.or()` across joined tables, so we
      // restrict the search to the activation row's machine_label here.
      // Licence-key / school-name search is best-effort client-side for
      // the currently visible page.
      query = query.ilike("machine_label", `%${safe}%`);
    }

    const { data, count, error: fetchError } = await query;

    if (fetchError) {
      console.error("Failed to fetch activations:", fetchError);
      setError("Failed to load activations: " + fetchError.message);
      setRows([]);
      setTotal(0);
    } else {
      setRows(
        (data ?? []).map((a: Record<string, unknown>) => {
          const licence = a.licenses as
            | {
                license_key: string;
                account_id: string;
                accounts?: { school_name: string } | null;
              }
            | null;
          return {
            id: a.id as string,
            license_id: a.license_id as string,
            machine_fingerprint: a.machine_fingerprint as string,
            machine_label: (a.machine_label as string | null) ?? null,
            activated_at: a.activated_at as string,
            deactivated_at: (a.deactivated_at as string | null) ?? null,
            license_key: licence?.license_key ?? "Unknown",
            school_name: licence?.accounts?.school_name ?? "Unknown",
            account_id: licence?.account_id ?? "",
          };
        }),
      );
      setTotal(count ?? 0);
    }
    setLoading(false);
  }, [page, debouncedSearch, showInternal, internalLicenseIds]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, showInternal]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  async function confirmDeactivate() {
    if (!pendingDeactivate) return;
    setBusy(true);
    const { error: updateError } = await supabase
      .from("activations")
      .update({ deactivated_at: new Date().toISOString() })
      .eq("id", pendingDeactivate.id);
    setBusy(false);

    if (updateError) {
      setError("Failed to deactivate: " + updateError.message);
      setPendingDeactivate(null);
      return;
    }
    setPendingDeactivate(null);
    await fetchRows();
  }

  const columns: AdminTableColumn<Activation>[] = [
    {
      key: "school",
      header: "School",
      render: (a) =>
        a.account_id ? (
          <Link
            to={`/admin/users/${a.account_id}`}
            className="font-medium text-forest-900 hover:underline"
          >
            {a.school_name}
          </Link>
        ) : (
          <span className="font-medium text-forest-900">{a.school_name}</span>
        ),
    },
    {
      key: "licence_key",
      header: "Licence",
      render: (a) => (
        <code className="font-mono text-xs text-forest-600">{a.license_key}</code>
      ),
    },
    {
      key: "machine",
      header: "Machine",
      render: (a) => (
        <div>
          <div className="text-forest-600">{a.machine_label ?? "(unnamed)"}</div>
          <div className="mt-0.5 font-mono text-[11px] text-forest-400">
            {a.machine_fingerprint.slice(0, 16)}&hellip;
          </div>
        </div>
      ),
    },
    {
      key: "activated",
      header: "Activated",
      render: (a) => (
        <span className="text-forest-500">
          {new Date(a.activated_at).toLocaleDateString("en-GB")}
        </span>
      ),
    },
    {
      key: "action",
      header: "",
      align: "right",
      render: (a) => (
        <button
          onClick={() => setPendingDeactivate(a)}
          className="inline-flex items-center rounded-lg border border-red-200 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-50"
        >
          Deactivate
        </button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Activations"
        description="Every machine currently bound to a licence. Deactivating one frees the licence for re-activation elsewhere."
        actions={
          <>
            <FilterToggle
              label="Show internal users"
              checked={showInternal}
              onChange={setShowInternal}
            />
            <SearchBar
              value={search}
              onChange={setSearch}
              placeholder="Search machine label"
            />
          </>
        }
      />

      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

      <AdminTable
        columns={columns}
        rows={rows}
        loading={loading}
        emptyMessage={
          debouncedSearch
            ? `No activations match "${debouncedSearch}".`
            : "No active activations."
        }
      />

      <Pagination
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
      />

      <ConfirmModal
        open={pendingDeactivate !== null}
        title="Deactivate this machine?"
        description={
          pendingDeactivate && (
            <>
              The activation for{" "}
              <span className="font-medium text-forest-900">
                {pendingDeactivate.school_name}
              </span>{" "}
              on{" "}
              <span className="font-mono text-forest-700">
                {pendingDeactivate.machine_label ?? "(unnamed machine)"}
              </span>{" "}
              will be marked deactivated. The customer will be prompted to
              re-activate on their next app launch.
            </>
          )
        }
        confirmLabel="Deactivate"
        confirmTone="red"
        onConfirm={confirmDeactivate}
        onCancel={() => setPendingDeactivate(null)}
        busy={busy}
      />
    </div>
  );
}
